import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import { PAYROLL_TARGET_FILTER } from './payrollEligibility';
import { stdTabGroup, stdTabBtn } from '../adminlayout/designStandard';

const cardStyle = {
  background: 'var(--bg-card)',
  borderRadius: 12,
  border: '1px solid var(--border)',
  boxShadow: 'none',
  padding: '16px 20px',
};
const btn = {
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '7px 12px',
  background: 'var(--bg-card)',
  color: 'var(--text-sub)',
  fontWeight: 800,
  fontSize: 12,
  cursor: 'pointer',
};

function shiftMonth(year, month, diff) {
  const value = (year * 12) + (month - 1) + diff;
  return { year: Math.floor(value / 12), month: (value % 12) + 1 };
}

function monthKey(year, month) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function noteKey(userId, year, month) {
  return `${userId}:${monthKey(year, month)}`;
}

function isEligibleMonth(user, month) {
  const key = monthKey(month.year, month.month);
  return !Array.isArray(user.eligible_months) || user.eligible_months.includes(key);
}

export default function PayrollMonthlyNotes({ companyCode }) {
  const { showToast } = useToast();
  const [start, setStart] = useState(null); // default-month API 응답 전까진 null
  const [defaultReady, setDefaultReady] = useState(false);
  const [employmentFilter, setEmploymentFilter] = useState(PAYROLL_TARGET_FILTER);
  const [data, setData] = useState({ months: [], users: [], notes: [] });
  const [noteMap, setNoteMap] = useState({});
  const [dirty, setDirty] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [loading, setLoading] = useState(false);

  // default-month API 로 시작월 결정 (PayrollMonthly 와 동일).
  // 오늘이 그 달 pay_date 이전이면 그 달이 default — 6/10 이전이면 5월, 6/11~ 이면 6월.
  useEffect(() => {
    let alive = true;
    setDefaultReady(false);
    (async () => {
      if (!companyCode) { if (alive) setDefaultReady(true); return; }
      const res = await apiClient.get(`/api/admin/payroll/default-month?company_code=${companyCode}`);
      if (alive && res.ok && res.data?.data) {
        setStart({ year: Number(res.data.data.year), month: Number(res.data.data.month) });
      }
      if (alive) setDefaultReady(true);
    })();
    return () => { alive = false; };
  }, [companyCode]);

  const fetchNotes = useCallback(async () => {
    if (!start) return;
    setLoading(true);
    const res = await apiClient.get(
      `/api/admin/payroll/monthly/notes?company_code=${companyCode}&start_year=${start.year}&start_month=${start.month}&months=12&employment_filter=${employmentFilter}`,
    );
    if (res.ok && res.data?.data) {
      const next = res.data.data;
      const map = {};
      (next.notes || []).forEach((row) => {
        map[noteKey(row.user_id, row.year, row.month)] = row.memo || '';
      });
      setData(next);
      setNoteMap(map);
      setDirty({});
    } else {
      showToast(res.data?.message || '월 비고 조회 실패', 'error');
    }
    setLoading(false);
  }, [companyCode, employmentFilter, showToast, start]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const monthStatus = useMemo(() => {
    const map = {};
    data.months.forEach((m) => { map[monthKey(m.year, m.month)] = m; });
    return map;
  }, [data.months]);

  const saveNote = async (user, month) => {
    const key = noteKey(user.id, month.year, month.month);
    if (!dirty[key] || month.locked || !isEligibleMonth(user, month)) return;
    setSavingKey(key);
    const res = await apiClient.put('/api/admin/payroll/monthly/notes', {
      company_code: companyCode,
      user_id: user.id,
      year: month.year,
      month: month.month,
      memo: noteMap[key] || '',
    });
    setSavingKey(null);
    if (res.ok) {
      setDirty((prev) => ({ ...prev, [key]: false }));
    } else {
      showToast(res.data?.message || '월 비고 저장 실패', 'error');
      fetchNotes();
    }
  };

  const move = (diff) => setStart((prev) => (prev ? shiftMonth(prev.year, prev.month, diff) : prev));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <button style={btn} onClick={() => move(-1)} aria-label="이전 달" disabled={!start}><ChevronLeft size={16} /></button>
        <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--text)', minWidth: 190 }}>
          {start ? `${start.year}년 ${start.month}월부터 12개월` : (defaultReady ? '시작월을 불러올 수 없습니다.' : '로딩 중...')}
        </div>
        <button style={btn} onClick={() => move(1)} aria-label="다음 달" disabled={!start}><ChevronRight size={16} /></button>
        <div style={{ marginLeft: 'auto', ...stdTabGroup }}>
          <button style={stdTabBtn(employmentFilter === PAYROLL_TARGET_FILTER)} onClick={() => setEmploymentFilter(PAYROLL_TARGET_FILTER)}>급여대상</button>
          <button style={stdTabBtn(employmentFilter === 'all')} onClick={() => setEmploymentFilter('all')}>전체(퇴직 포함)</button>
        </div>
      </div>

      <div style={{ ...cardStyle, padding: 0, overflow: 'auto' }}>
        <table style={{ minWidth: 1180, width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 12 }}>
          <thead>
            <tr>
              <th style={headCell(true)}>성명</th>
              {data.months.map((m) => (
                <th key={monthKey(m.year, m.month)} style={headCell(false)}>
                  <div>{m.year}년 {m.month}월</div>
                  {m.locked && <span style={lockBadge}><Lock size={10} />확정</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>로딩 중...</td></tr>
            ) : data.users.length === 0 ? (
              <tr><td colSpan={13} style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>표시할 직원이 없습니다.</td></tr>
            ) : data.users.map((user, rowIndex) => (
              <tr key={user.id}>
                <td style={nameCell(rowIndex)}>
                  <div>{user.name}</div>
                  {user.resign_date && <span style={retiredBadge}>퇴직</span>}
                </td>
                {data.months.map((m) => {
                  const key = noteKey(user.id, m.year, m.month);
                  const locked = monthStatus[monthKey(m.year, m.month)]?.locked;
                  const eligible = isEligibleMonth(user, m);
                  const disabled = locked || !eligible;
                  return (
                    <td key={key} style={bodyCell(rowIndex, disabled)}>
                      <textarea
                        value={noteMap[key] || ''}
                        disabled={disabled}
                        title={locked ? '월 급여 관리에서 확정된 월은 비고를 수정할 수 없습니다.' : (!eligible ? '해당월 급여대상 직원이 아닙니다.' : '')}
                        onChange={(e) => {
                          setNoteMap((prev) => ({ ...prev, [key]: e.target.value }));
                          setDirty((prev) => ({ ...prev, [key]: true }));
                        }}
                        onBlur={() => saveNote(user, m)}
                        style={noteInput(disabled)}
                      />
                      {savingKey === key && <span style={savingText}>저장중</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function headCell(sticky) {
  return {
    position: sticky ? 'sticky' : 'static',
    left: sticky ? 0 : undefined,
    zIndex: sticky ? 2 : 1,
    top: 0,
    padding: '10px 8px',
    borderBottom: '1px solid var(--border)',
    borderRight: '1px solid var(--border)',
    background: '#f59e0b',
    color: '#fff',
    fontSize: 13,
    fontWeight: 900,
    textAlign: 'center',
    minWidth: sticky ? 110 : 136,
  };
}

function nameCell(index) {
  return {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    padding: '8px 10px',
    borderBottom: '1px dotted var(--border)',
    borderRight: '1px solid var(--border)',
    background: index % 2 ? 'var(--bg-subtle)' : 'var(--bg-card)',
    color: 'var(--text)',
    fontWeight: 900,
    textAlign: 'center',
    minWidth: 110,
  };
}

function bodyCell(index, locked) {
  return {
    position: 'relative',
    padding: 4,
    borderBottom: '1px dotted var(--border)',
    borderRight: '1px solid var(--border)',
    background: locked ? 'rgba(148,163,184,0.12)' : (index % 2 ? 'var(--bg-subtle)' : 'var(--bg-card)'),
  };
}

function noteInput(locked) {
  return {
    width: '100%',
    minHeight: 46,
    resize: 'vertical',
    border: 'none',
    outline: 'none',
    background: 'transparent',
    color: locked ? 'var(--text-muted)' : 'var(--text)',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.35,
  };
}

const lockBadge = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  marginTop: 3,
  padding: '1px 5px',
  borderRadius: 999,
  background: 'rgba(255,255,255,0.24)',
  fontSize: 10,
};
const retiredBadge = { display: 'block', marginTop: 2, fontSize: 10, color: '#dc2626', fontWeight: 800 };
const savingText = { position: 'absolute', right: 6, bottom: 4, fontSize: 10, color: 'var(--std-accent-text, #1d7de6)', fontWeight: 800 };
